import { captureException } from "@sentry/browser";

/**
 * Wraps a function so that any thrown error is reported to Sentry before re-throwing.
 * Works with sync functions, async functions, and sync functions that return a Promise.
 *
 * @param {(...args: any[]) => any} fn - The function to wrap with error capture.
 * @returns {(...args: any[]) => any} A new function with identical behaviour plus Sentry reporting.
 */
export function tryCatch(fn) {
    // TODO: The wrapper invokes `fn(...args)` rather than `fn.apply(this, args)`, so a
    // method wrapped directly loses its receiver and throws on the first `this` access.
    // Every caller currently passes an arrow function that closes over `this`, which is
    // why this has not bitten yet. Switch both branches to `.apply(this, args)` (and
    // `withGuard` below with it) tests/js/webshop/helpers/middleware.test.js pins the current
    // behaviour and will need updating at the same time.
    if (fn.constructor.name === "AsyncFunction") {
        return async function (/** @type {any[]} */ ...args) {
            try {
                return await fn(...args);
            } catch (/** @type {any} */ error) {
                captureException(error);
                throw error;
            }
        };
    } else {
        return function (/** @type {any[]} */ ...args) {
            try {
                const result = fn(...args);

                if (result && typeof result.then === "function") {
                    return result.catch((/** @type {any} */ error) => {
                        captureException(error);
                        throw error;
                    });
                }

                return result;
            } catch (/** @type {any} */ error) {
                captureException(error);
                throw error;
            }
        };
    }
}

/**
 * Composes a series of guard steps into a single async function.
 * Each step is called in order. If any step returns exactly `false`, execution stops.
 *
 * @param {Array<((...args: any[]) => any)>} steps - Guard functions to run sequentially.
 * @returns {(...args: any[]) => Promise<void>} An async function that runs all steps or bails on `false`.
 */
export function withGuard(...steps) {
    return async function (/** @type {any[]} */ ...args) {
        for (const step of steps) {
            if (typeof step === "function") {
                const result =
                    step.constructor.name === "AsyncFunction"
                        ? await step(...args)
                        : step(...args);

                if (typeof result === "boolean" && !result) {
                    return;
                }
            }
        }
    };
}
